import React, { useEffect, useState } from "react";
import { Polyline, CircleMarker, Tooltip, useMapEvents } from "react-leaflet";
import { Ruler } from "@phosphor-icons/react";

const LINE_STYLE = { color: "#4CC9F0", weight: 3, dashArray: "8 6", opacity: 0.9 };
const DOT_STYLE = { color: "#4CC9F0", weight: 2, fillColor: "#0F1311", fillOpacity: 1 };

function fmtMi(m) {
  const mi = m / 1609.344;
  return mi >= 10 ? `${mi.toFixed(1)} mi` : `${mi.toFixed(2)} mi`;
}

function MeasureRun({ map, points, cursor = null }) {
  const preview = points.length === 1 && cursor;
  const line = preview ? [points[0], cursor] : points;
  let total = 0;
  for (let i = 1; i < points.length; i += 1) total += map.distance(points[i - 1], points[i]);
  const liveTotal = preview ? map.distance(points[0], cursor) : total;

  if (!points.length) return null;
  const last = line[line.length - 1];

  return (
    <>
      {line.length > 1 && <Polyline positions={line} pathOptions={LINE_STYLE} interactive={false} />}
      {points.map((p, i) => (
        <CircleMarker key={i} center={p} radius={4} pathOptions={DOT_STYLE} interactive={false}>
          {i > 0 && i < points.length - 1 && (
            <Tooltip permanent direction="top" offset={[0, -6]} className="measure-tip">
              {fmtMi(points.slice(0, i + 1).reduce((acc, _, j) => (j ? acc + map.distance(points[j - 1], points[j]) : 0), 0))}
            </Tooltip>
          )}
        </CircleMarker>
      ))}
      {line.length > 1 && (
        <CircleMarker center={last} radius={5} pathOptions={{ ...DOT_STYLE, fillColor: "#4CC9F0" }} interactive={false}>
          <Tooltip permanent direction="right" offset={[8, 0]} className="measure-tip measure-tip-total">
            {fmtMi(liveTotal)}
          </Tooltip>
        </CircleMarker>
      )}
    </>
  );
}

export function MeasureLayer({ onTotal, onDrawing, finishTick = 0 }) {
  const [runs, setRuns] = useState([]);
  const [points, setPoints] = useState([]);
  const [cursor, setCursor] = useState(null);

  const finishRun = () => {
    // Finish this measurement; the next click starts a fresh one.
    let trimmed = points;
    while (
      trimmed.length >= 2 &&
      map.distance(trimmed[trimmed.length - 1], trimmed[trimmed.length - 2]) < 2
    ) {
      trimmed = trimmed.slice(0, -1);
    }
    if (trimmed.length > 1) setRuns((r) => [...r, trimmed]);
    setPoints([]);
    setCursor(null);
  };

  const map = useMapEvents({
    click(e) {
      setPoints((p) => [...p, e.latlng]);
      setCursor(null);
    },
    dblclick() {
      finishRun();
    },
    mousemove(e) {
      // Rubber-band only between the start click and the next reference click
      if (points.length === 1) setCursor(e.latlng);
    },
  });

  // Toolbar "New Line" button — same effect as a double-click.
  useEffect(() => {
    if (finishTick > 0) finishRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishTick]);

  useEffect(() => {
    onDrawing?.(points.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points.length]);

  useEffect(() => {
    map.doubleClickZoom.disable();
    const container = map.getContainer();
    container.style.cursor = "crosshair";
    const scaleTips = () => {
      const z = map.getZoom();
      container.style.setProperty("--measure-font", `${Math.min(20, Math.max(12, 6 + z))}px`);
    };
    scaleTips();
    map.on("zoomend", scaleTips);
    return () => {
      map.doubleClickZoom.enable();
      map.off("zoomend", scaleTips);
      container.style.cursor = "";
    };
  }, [map]);

  const preview = points.length === 1 && cursor;
  let total = 0;
  for (let i = 1; i < points.length; i += 1) total += map.distance(points[i - 1], points[i]);
  const liveTotal = preview ? map.distance(points[0], cursor) : total;
  const hasLive = points.length > 1 || preview;

  useEffect(() => {
    if (hasLive) onTotal?.(fmtMi(liveTotal));
    else if (runs.length) onTotal?.(`${runs.length} measured`);
    else onTotal?.(null);
  });

  return (
    <>
      {runs.map((r, i) => (
        <MeasureRun key={`run-${i}`} map={map} points={r} />
      ))}
      <MeasureRun map={map} points={points} cursor={cursor} />
    </>
  );
}

export function MeasureButton({ active, onToggle, total, drawing = false, onFinish }) {
  return (
    <>
      <button
        type="button"
        data-testid="map-measure-btn"
        title="Measure distance in miles — click to add points, double-click (or the New Line button) to finish a line and start another. Click ruler to clear all."
        onClick={onToggle}
        className={`px-2.5 py-1.5 rounded-sm border backdrop-blur-xl transition-colors flex items-center gap-1.5 whitespace-nowrap ${
          active
            ? "bg-[#4CC9F0]/20 border-[#4CC9F0] text-[#4CC9F0]"
            : "bg-tac-bg/85 border-tac-border text-tac-text hover:border-tac-borderBright"
        }`}
      >
        <Ruler size={14} weight="bold" />
        {active && (
          <span className="text-[10px] font-bold uppercase tracking-wider" data-testid="map-measure-total">
            {total || "Click map"}
          </span>
        )}
      </button>
      {active && drawing && (
        <button
          type="button"
          data-testid="map-measure-finish-btn"
          title="Finish this measurement — your next click starts a new tape"
          onClick={onFinish}
          className="px-2.5 py-1.5 rounded-sm border backdrop-blur-xl transition-colors flex items-center gap-1 whitespace-nowrap bg-[#4CC9F0] border-[#4CC9F0] text-[#0F1311] font-bold text-[10px] uppercase tracking-wider hover:bg-[#7AD7FF]"
        >
          ✓ New Line
        </button>
      )}
    </>
  );
}
